import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import Swal from 'sweetalert2';
import financialService from '../../services/financial';
import appointmentsService from '../../services/appointments';
import patientsService from '../../services/patients';

ChartJS.register(
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

interface FinancialStats {
  totalExpected: number;
  totalReceived: number;
  totalPending: number;
  scheduledCount: number;
  completedCount: number;
  scheduledSum: number;
  completedSum: number;
}

interface PaymentProjection {
  period: string; // YYYY-MM-DD
  expectedAmount: number;
  expectedCount: number;
  receivedAmount: number;
  receivedCount: number;
}

interface Appointment {
  id: string;
  personId: string;
  personName?: string;
  dateTime: string;
  status: string;
  paymentStatus: string;
  paymentValue?: number;
  consultationValue?: number;
  expectedPaymentDate?: string;
  paymentDate?: string;
  paymentProof?: string;
  invoiceIssued: boolean;
  invoiceNumber?: string;
}

interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  issuesInvoice: boolean;
}

const FinancialBalance: React.FC = () => {
  const [stats, setStats] = useState<FinancialStats | null>(null);
  const [projection, setProjection] = useState<PaymentProjection[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [totalAppointments, setTotalAppointments] = useState(0);
  
  // Estados para autocomplete de pacientes
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState<Patient[]>([]);
  const [showPatientSuggestions, setShowPatientSuggestions] = useState(false);
  const [isSearchingPatients, setIsSearchingPatients] = useState(false);
  const [selectedPatientName, setSelectedPatientName] = useState('');
  
  // Estados para minimizar/expandir gráficos
  const [showFinancialChart, setShowFinancialChart] = useState(true);
  const [showStatusChart, setShowStatusChart] = useState(true);
  const [showProjectionChart, setShowProjectionChart] = useState(true);
  
  const [filters, setFilters] = useState({
    personId: '',
    appointmentStatus: '', // SCHEDULED, COMPLETED, CANCELLED
    paymentStatus: '', // PENDING, PAID
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    //ultimo dia do mes
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  });

  // Debounce para busca de pacientes
  useEffect(() => {
    if (!patientSearchTerm || patientSearchTerm.length < 2) {
      setPatientSearchResults([]);
      setShowPatientSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearchingPatients(true);
        const results = await patientsService.getAll({ search: patientSearchTerm });
        setPatientSearchResults(results);
        setShowPatientSuggestions(true);
      } catch (error) {
        console.error('Erro ao buscar pacientes:', error);
      } finally {
        setIsSearchingPatients(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [patientSearchTerm]);

  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.patient-autocomplete')) {
        setShowPatientSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    if (patients.length > 0) {
      setCurrentPage(1); // Reset para primeira página ao mudar filtros
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate, filters.personId, filters.appointmentStatus, filters.paymentStatus, patients.length]);

  // Carregar dados quando a página mudar (exceto na primeira carga)
  useEffect(() => {
    if (patients.length > 0 && currentPage > 0) {
      // Só recarrega appointments se não for a carga inicial
      const isInitialLoad = currentPage === 1 && appointments.length === 0;
      if (!isInitialLoad) {
        loadAppointmentsData(currentPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadPatients = async () => {
    try {
      const data = await patientsService.getAll();
      setPatients(data);
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Converter datas para formato YYYY-MM-DD
      const startDateStr = filters.startDate.toISOString().split('T')[0];
      const endDateStr = filters.endDate.toISOString().split('T')[0];
      
      // Buscar estatísticas
      const statsData = await financialService.getFinancialStats({
        personId: filters.personId,
        startDate: startDateStr,
        endDate: endDateStr
      });
      setStats(statsData);

      // Buscar projeção de pagamentos
      const projectionData = await financialService.getPaymentProjection({
        personId: filters.personId,
        startDate: startDateStr,
        endDate: endDateStr
      });
      setProjection(projectionData || []);

      // Carregar appointments paginados
      await loadAppointmentsData(1);
    } catch (error) {
      console.error('Erro ao carregar dados financeiros:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível carregar os dados financeiros'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAppointmentsData = async (page: number) => {
    try {
      // Converter datas para formato YYYY-MM-DD
      const startDateStr = filters.startDate.toISOString().split('T')[0];
      const endDateStr = filters.endDate.toISOString().split('T')[0];

      // Buscar agendamentos com paginação e filtros
      const response = await appointmentsService.getAll({
        startDate: startDateStr,
        endDate: endDateStr,
        personId: filters.personId || undefined,
        status: filters.appointmentStatus || undefined,
        paymentStatus: filters.paymentStatus || undefined,
        page: page,
        pageSize: pageSize
      });

      // Verificar se a resposta tem estrutura de paginação
      let appointmentsData = [];
      let total = 0;

      if (response && typeof response === 'object' && 'data' in response) {
        // Resposta com paginação
        appointmentsData = response.data || [];
        total = response.total || 0;
      } else {
        // Resposta sem paginação (array direto)
        appointmentsData = Array.isArray(response) ? response : [];
        total = appointmentsData.length;
      }

      // Enriquecer com dados do paciente
      const enriched = appointmentsData.map((apt: any) => {
        const patient = patients.find(p => p.id === apt.personId);
        return {
          ...apt,
          personName: patient ? `${patient.firstName} ${patient.lastName}` : 'Desconhecido',
          issuesInvoice: patient?.issuesInvoice || false
        };
      });

      setAppointments(enriched);
      setTotalAppointments(total);
      setTotalPages(Math.ceil(total / pageSize));
      setCurrentPage(page);
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível carregar os agendamentos'
      });
    }
  };

  const handleMarkAsPaid = async (appointmentId: string, currentDate?: string) => {
    // Preparar a data inicial
    let initialDate = new Date();
    if (currentDate) {
      try {
        initialDate = new Date(currentDate);
      } catch (e) {
        initialDate = new Date();
      }
    }
    const formattedDate = initialDate.toLocaleDateString('pt-BR');

    const { value: formValues } = await Swal.fire({
      title: 'Marcar como Pago',
      html: `
        <div class="form-group text-left mb-3">
          <label for="paymentDate" class="form-label">Data do Pagamento (dd/mm/aaaa)</label>
          <input type="text" id="paymentDate" class="form-control" value="${formattedDate}" placeholder="dd/mm/aaaa" maxlength="10">
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      didOpen: () => {
        const input = document.getElementById('paymentDate') as HTMLInputElement;
        // Máscara de data
        input.addEventListener('input', (e: any) => {
          let value = e.target.value.replace(/\D/g, '');
          if (value.length >= 2) {
            value = value.substring(0, 2) + '/' + value.substring(2);
          }
          if (value.length >= 5) {
            value = value.substring(0, 5) + '/' + value.substring(5, 9);
          }
          e.target.value = value;
        });
      },
      preConfirm: () => {
        const dateInput = document.getElementById('paymentDate') as HTMLInputElement;
        const dateValue = dateInput.value;
        
        // Validar formato dd/mm/yyyy
        const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
        const match = dateValue.match(dateRegex);
        
        if (!match) {
          Swal.showValidationMessage('Data inválida. Use o formato dd/mm/aaaa');
          return false;
        }
        
        const [, day, month, year] = match;
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        
        if (isNaN(date.getTime())) {
          Swal.showValidationMessage('Data inválida');
          return false;
        }
        
        return {
          paymentDate: `${year}-${month}-${day}`
        };
      }
    });

    if (formValues) {
      try {
        const paymentDate = new Date(formValues.paymentDate + 'T12:00:00').toISOString();
        
        await financialService.updatePayment(appointmentId, {
          paymentStatus: 'PAID',
          paymentDate: paymentDate
        });

        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Pagamento registrado com sucesso',
          timer: 2000,
          showConfirmButton: false
        });

        loadData();
      } catch (error) {
        console.error('Erro ao marcar como pago:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Não foi possível registrar o pagamento'
        });
      }
    }
  };

  const handleInvoice = async (appointmentId: string, currentInvoiceNumber?: string) => {
    const { value: formValues } = await Swal.fire({
      title: 'Nota Fiscal',
      html: `
        <div class="form-group text-left mb-3">
          <label for="invoiceNumber" class="form-label">Número da Nota</label>
          <input type="text" id="invoiceNumber" class="form-control" value="${currentInvoiceNumber || ''}" placeholder="Ex: 001234">
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Salvar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const invoiceInput = document.getElementById('invoiceNumber') as HTMLInputElement;
        return {
          invoiceNumber: invoiceInput.value
        };
      }
    });

    if (formValues && formValues.invoiceNumber) {
      try {
        await financialService.updatePayment(appointmentId, {
          paymentStatus: 'PAID',
          invoiceIssued: true,
          invoiceNumber: formValues.invoiceNumber
        });

        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Nota fiscal registrada com sucesso',
          timer: 2000,
          showConfirmButton: false
        });

        loadData();
      } catch (error) {
        console.error('Erro ao registrar nota:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Não foi possível registrar a nota fiscal'
        });
      }
    }
  };

  const handleChangeStatus = async (appointmentId: string, newStatus: string) => {
    const statusLabels: any = {
      'SCHEDULED': 'Agendado',
      'COMPLETED': 'Concluído',
      'CANCELLED': 'Cancelado'
    };

    const result = await Swal.fire({
      title: 'Alterar Status',
      text: `Deseja marcar este atendimento como ${statusLabels[newStatus]}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, alterar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        console.log('Alterando status para:', newStatus, 'do appointment:', appointmentId);
        const response = await appointmentsService.update(appointmentId, { status: newStatus });
        console.log('Resposta do backend:', response);
        
        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Status alterado com sucesso',
          timer: 2000,
          showConfirmButton: false
        });

        loadData();
      } catch (error) {
        console.error('Erro ao alterar status:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Não foi possível alterar o status'
        });
      }
    }
  };

  const handleRemovePayment = async (appointmentId: string) => {
    const result = await Swal.fire({
      title: 'Remover Pagamento',
      text: 'Deseja remover o registro de pagamento deste atendimento?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        const paymentData = {
          paymentStatus: 'PENDING',
          paymentDate: '',
          invoiceIssued: false,
          invoiceNumber: ''
        };
        
        console.log('Removendo pagamento com dados:', paymentData);
        await financialService.updatePayment(appointmentId, paymentData);

        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Pagamento removido com sucesso',
          timer: 2000,
          showConfirmButton: false
        });

        loadData();
      } catch (error) {
        console.error('Erro ao remover pagamento:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Não foi possível remover o pagamento'
        });
      }
    }
  };

  const handleRemoveInvoice = async (appointmentId: string) => {
    const result = await Swal.fire({
      title: 'Remover Nota Fiscal',
      text: 'Deseja remover a nota fiscal deste atendimento?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        await financialService.updatePayment(appointmentId, {
          invoiceIssued: false,
          invoiceNumber: ''
        });

        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Nota fiscal removida com sucesso',
          timer: 2000,
          showConfirmButton: false
        });

        loadData();
      } catch (error) {
        console.error('Erro ao remover nota:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Não foi possível remover a nota fiscal'
        });
      }
    }
  };

  const formatCurrency = (value?: number) => {
    if (!value && value !== 0) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return '-';
    }
  };

  // Dados para o gráfico de Esperado vs Recebido
  // Dados para o gráfico de pizza (Valores Financeiros)
  const financialPieData = {
    labels: ['Esperado', 'Recebido', 'Pendente'],
    datasets: [
      {
        data: [
          stats?.totalExpected || 0,
          stats?.totalReceived || 0,
          stats?.totalPending || 0
        ],
        backgroundColor: [
          'rgba(54, 162, 235, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(255, 206, 86, 0.5)',
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 206, 86, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const financialPieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed || 0;
            return `${label}: ${formatCurrency(value)}`;
          }
        }
      }
    }
  };

  // Dados para o gráfico de pizza (Status)
  const statusPieData = {
    labels: ['Agendados', 'Concluídos'],
    datasets: [
      {
        data: [stats?.scheduledCount || 0, stats?.completedCount || 0],
        backgroundColor: [
          'rgba(255, 159, 64, 0.5)',
          'rgba(75, 192, 192, 0.5)',
        ],
        borderColor: [
          'rgba(255, 159, 64, 1)',
          'rgba(75, 192, 192, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const statusPieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed || 0;
            return `${label}: ${value} consulta${value !== 1 ? 's' : ''}`;
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <>
        <div className="content-header">
          <div className="container-fluid">
            <h1 className="m-0">Balanço Financeiro</h1>
          </div>
        </div>
        <section className="content">
          <div className="container-fluid">
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">Carregando...</span>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">Balanço Financeiro</h1>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          {/* Filtros */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Filtros</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Paciente (opcional)</label>
                    <div className="patient-autocomplete" style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Digite para buscar..."
                        value={patientSearchTerm}
                        onChange={(e) => {
                          setPatientSearchTerm(e.target.value);
                          if (!e.target.value) {
                            setFilters({ ...filters, personId: '' });
                            setSelectedPatientName('');
                          }
                        }}
                        autoComplete="off"
                      />
                      {isSearchingPatients && (
                        <div style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }}>
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="sr-only">Buscando...</span>
                          </div>
                        </div>
                      )}
                      {showPatientSuggestions && patientSearchResults.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ced4da',
                            borderRadius: '0.25rem',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          {patientSearchResults.map((patient) => (
                            <div
                              key={patient.id}
                              style={{
                                padding: '10px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                              }}
                              onClick={() => {
                                setFilters({ ...filters, personId: patient.id });
                                setSelectedPatientName(`${patient.firstName} ${patient.lastName}`);
                                setPatientSearchTerm(`${patient.firstName} ${patient.lastName}`);
                                setShowPatientSuggestions(false);
                              }}
                            >
                              <div style={{ fontWeight: 500 }}>
                                {patient.firstName} {patient.lastName}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {selectedPatientName && (
                        <small className="form-text text-muted">
                          Selecionado: {selectedPatientName}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="form-group">
                    <label>Status Atendimento</label>
                    <select
                      className="form-control"
                      value={filters.appointmentStatus}
                      onChange={(e) => setFilters({ ...filters, appointmentStatus: e.target.value })}
                    >
                      <option value="">Todos</option>
                      <option value="SCHEDULED">Agendado</option>
                      <option value="COMPLETED">Concluído</option>
                      <option value="CANCELLED">Cancelado</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="form-group">
                    <label>Status Pagamento</label>
                    <select
                      className="form-control"
                      value={filters.paymentStatus}
                      onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
                    >
                      <option value="">Todos</option>
                      <option value="PENDING">Pendente</option>
                      <option value="PAID">Pago</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="form-group">
                    <label>Data Início</label>
                    <DatePicker
                      selected={filters.startDate}
                      onChange={(date: Date | null) => date && setFilters({ ...filters, startDate: date })}
                      dateFormat="dd/MM/yyyy"
                      locale={ptBR}
                      className="form-control"
                      wrapperClassName="d-block"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Data Fim</label>
                    <DatePicker
                      selected={filters.endDate}
                      onChange={(date: Date | null) => date && setFilters({ ...filters, endDate: date })}
                      dateFormat="dd/MM/yyyy"
                      locale={ptBR}
                      className="form-control"
                      wrapperClassName="d-block"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cards de Estatísticas */}
          <div className="row">
            <div className="col-lg-3 col-6">
              <div className="small-box bg-info">
                <div className="inner">
                  <h3>{formatCurrency(stats?.totalExpected || 0)}</h3>
                  <p>Total Esperado</p>
                </div>
                <div className="icon">
                  <i className="fas fa-calculator"></i>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-6">
              <div className="small-box bg-success">
                <div className="inner">
                  <h3>{formatCurrency(stats?.totalReceived || 0)}</h3>
                  <p>Total Recebido</p>
                </div>
                <div className="icon">
                  <i className="fas fa-check-circle"></i>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-6">
              <div className="small-box bg-warning">
                <div className="inner">
                  <h3>{formatCurrency(stats?.totalPending || 0)}</h3>
                  <p>Total Pendente</p>
                </div>
                <div className="icon">
                  <i className="fas fa-clock"></i>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-6">
              <div className="small-box bg-secondary">
                <div className="inner">
                  <h3>
                    {stats?.completedCount || 0}/
                    {(stats?.scheduledCount || 0) + (stats?.completedCount || 0)}
                  </h3>
                  <p>Consultas Concluídas</p>
                </div>
                <div className="icon">
                  <i className="fas fa-calendar-check"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Gráficos */}
          <div className="row">
            <div className="col-md-6">
              <div className="card mb-3">
                <div className="card-header">
                  <h3 className="card-title">Distribuição Financeira</h3>
                  <div className="card-tools">
                    <button
                      type="button"
                      className="btn btn-tool"
                      onClick={() => setShowFinancialChart(!showFinancialChart)}
                    >
                      <i className={`fas fa-${showFinancialChart ? 'minus' : 'plus'}`}></i>
                    </button>
                  </div>
                </div>
                {showFinancialChart && (
                  <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                    <div style={{ width: '100%', maxWidth: '350px', height: '350px' }}>
                      <Doughnut data={financialPieData} options={financialPieOptions} />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="card mb-3">
                <div className="card-header">
                  <h3 className="card-title">Status das Consultas</h3>
                  <div className="card-tools">
                    <button
                      type="button"
                      className="btn btn-tool"
                      onClick={() => setShowStatusChart(!showStatusChart)}
                    >
                      <i className={`fas fa-${showStatusChart ? 'minus' : 'plus'}`}></i>
                    </button>
                  </div>
                </div>
                {showStatusChart && (
                  <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                    <div style={{ width: '100%', maxWidth: '350px', height: '350px' }}>
                      <Doughnut data={statusPieData} options={statusPieOptions} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Gráfico de Linha */}
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Projeção de Recebimentos</h3>
                  <div className="card-tools">
                    <button
                      type="button"
                      className="btn btn-tool"
                      onClick={() => setShowProjectionChart(!showProjectionChart)}
                    >
                      <i className={`fas fa-${showProjectionChart ? 'minus' : 'plus'}`}></i>
                    </button>
                  </div>
                </div>
                {showProjectionChart && (
                  <div className="card-body">
                  {projection.length > 0 ? (
                    <Line 
                      data={{
                        labels: projection.map(p => {
                          const [, month, day] = p.period.split('-');
                          return `${day}/${month}`;
                        }),
                        datasets: [
                          {
                            label: 'Valor Esperado',
                            data: projection.map(p => p.expectedAmount),
                            borderColor: 'rgba(54, 162, 235, 1)',
                            backgroundColor: 'rgba(54, 162, 235, 0.1)',
                            borderWidth: 2,
                            fill: true,
                            tension: 0.4,
                            pointRadius: 5,
                            pointHoverRadius: 7,
                            pointBackgroundColor: 'rgba(54, 162, 235, 1)',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                          },
                          {
                            label: 'Valor Recebido',
                            data: projection.map(p => p.receivedAmount),
                            borderColor: 'rgba(75, 192, 192, 1)',
                            backgroundColor: 'rgba(75, 192, 192, 0.1)',
                            borderWidth: 2,
                            fill: true,
                            tension: 0.4,
                            pointRadius: 5,
                            pointHoverRadius: 7,
                            pointBackgroundColor: 'rgba(75, 192, 192, 1)',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: true,
                            position: 'bottom'
                          },
                          tooltip: {
                            callbacks: {
                              label: (context: any) => {
                                const datasetLabel = context.dataset.label;
                                const value = context.parsed.y || 0;
                                const dataPoint = projection[context.dataIndex];
                                
                                if (datasetLabel === 'Valor Esperado') {
                                  return [
                                    `Esperado: R$ ${value.toFixed(2)}`,
                                    `Consultas: ${dataPoint.expectedCount}`
                                  ];
                                } else {
                                  return [
                                    `Recebido: R$ ${value.toFixed(2)}`,
                                    `Consultas: ${dataPoint.receivedCount}`
                                  ];
                                }
                              }
                            }
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              callback: function(value: any) {
                                return 'R$ ' + value.toFixed(2);
                              }
                            }
                          }
                        }
                      }}
                      height={550}
                    />
                  ) : (
                    <div className="info-box">
                      <span className="info-box-icon bg-info"><i className="fas fa-calendar-alt"></i></span>
                      <div className="info-box-content">
                        <span className="info-box-text">Sem dados</span>
                        <span className="info-box-number">Nenhum pagamento pendente</span>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabela de Agendamentos */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Atendimentos do Período</h3>
            </div>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover text-nowrap">
                <thead>
                  <tr>
                    <th>Data/Hora</th>
                    <th>Paciente</th>
                    <th>Status</th>
                    <th>Valor</th>
                    <th>Previsão Pgto</th>
                    <th>Data Pgto</th>
                    <th>Status Pgto</th>
                    <th>Nota Fiscal</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map(apt => (
                    <tr key={apt.id}>
                      <td>{formatDate(apt.dateTime)}</td>
                      <td>{apt.personName}</td>
                      <td>
                        {apt.status ? (
                          <span className={`badge ${apt.status === 'COMPLETED' ? 'badge-success' : apt.status === 'SCHEDULED' ? 'badge-info' : 'badge-secondary'}`}>
                            {apt.status === 'COMPLETED' ? 'Concluído' : apt.status === 'SCHEDULED' ? 'Agendado' : apt.status}
                          </span>
                        ) : (
                          <span className="badge badge-secondary">N/A</span>
                        )}
                      </td>
                      <td>{formatCurrency(apt.paymentValue || apt.consultationValue || 0)}</td>
                      <td>{formatDate(apt.expectedPaymentDate)}</td>
                      <td>{formatDate(apt.paymentDate)}</td>
                      <td>
                        {apt.paymentStatus ? (
                          <span className={`badge ${apt.paymentStatus === 'PAID' ? 'badge-success' : 'badge-warning'}`}>
                            {apt.paymentStatus === 'PAID' ? 'PAGO' : 'PENDENTE'}
                          </span>
                        ) : (
                          <span className="badge badge-warning">PENDENTE</span>
                        )}
                      </td>
                      <td>
                        {apt.invoiceIssued ? (
                          <span className="badge badge-success">
                            NF {apt.invoiceNumber}
                          </span>
                        ) : (
                          <span className="badge badge-secondary">Não emitida</span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group-vertical btn-group-sm" role="group">
                          {/* Linha 1: Status e Pagamento */}
                          <div className="btn-group btn-group-sm mb-1" role="group">
                            {/* Botões de Status */}
                            {apt.status !== 'COMPLETED' && (
                              <button
                                className="btn btn-success"
                                onClick={() => handleChangeStatus(apt.id, 'COMPLETED')}
                                title="Marcar como Concluído"
                              >
                                <i className="fas fa-check-circle"></i>
                              </button>
                            )}
                            {apt.status !== 'CANCELLED' && (
                              <button
                                className="btn btn-danger"
                                onClick={() => handleChangeStatus(apt.id, 'CANCELLED')}
                                title="Marcar como Cancelado"
                              >
                                <i className="fas fa-times-circle"></i>
                              </button>
                            )}
                            {apt.status !== 'SCHEDULED' && (
                              <button
                                className="btn btn-info"
                                onClick={() => handleChangeStatus(apt.id, 'SCHEDULED')}
                                title="Marcar como Agendado"
                              >
                                <i className="fas fa-calendar"></i>
                              </button>
                            )}
                          </div>

                          {/* Linha 2: Ações de Pagamento */}
                          <div className="btn-group btn-group-sm" role="group">
                            {apt.paymentStatus !== 'PAID' ? (
                              <button
                                className="btn btn-success"
                                onClick={() => handleMarkAsPaid(apt.id, apt.paymentDate)}
                                title="Marcar como Pago"
                              >
                                <i className="fas fa-dollar-sign"></i>
                              </button>
                            ) : (
                              <button
                                className="btn btn-warning"
                                onClick={() => handleRemovePayment(apt.id)}
                                title="Remover Pagamento"
                              >
                                <i className="fas fa-undo"></i>
                              </button>
                            )}

                            {/* Nota Fiscal */}
                            {(apt as any).issuesInvoice && apt.paymentStatus === 'PAID' && (
                              <>
                                <button
                                  className="btn btn-info"
                                  onClick={() => handleInvoice(apt.id, apt.invoiceNumber)}
                                  title={apt.invoiceIssued ? "Editar Nota Fiscal" : "Emitir Nota Fiscal"}
                                >
                                  <i className="fas fa-file-invoice"></i>
                                </button>
                                {apt.invoiceIssued && (
                                  <button
                                    className="btn btn-secondary"
                                    onClick={() => handleRemoveInvoice(apt.id)}
                                    title="Remover Nota Fiscal"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Controles de paginação */}
            {totalPages > 1 && (
              <div className="card-footer clearfix">
                <div className="row align-items-center">
                  <div className="col-sm-6">
                    <p className="mb-0">
                      Mostrando {appointments.length > 0 ? ((currentPage - 1) * pageSize + 1) : 0} a {Math.min(currentPage * pageSize, totalAppointments)} de {totalAppointments} atendimentos
                    </p>
                  </div>
                  <div className="col-sm-6">
                    <ul className="pagination pagination-sm float-right mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        >
                          «
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          ‹
                        </button>
                      </li>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          </li>
                        );
                      })}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          ›
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                        >
                          »
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default FinancialBalance;
